/**
 * Programs API utilities
 */

import { BackendServicesResponse, ProgramsApiResponse } from '@/lib/mappers/programsMapper';
import { fetchApi } from './client';
import { serverFetch } from './serverFetch';

/**
 * Fetches programs data from the real backend API
 */
export async function fetchProgramsData(): Promise<BackendServicesResponse> {
  const backendUrl = process.env.NEXT_PUBLIC_API_BACKEND || 'https://sca-backend.digitaldevworkspace.com';
  const apiUrl = `${backendUrl}/api/v1/services?limit=3`;
  
  console.log('🔍 [Programs API] Client-side fetch starting...');
  console.log('🔍 [Programs API] Backend URL:', backendUrl);
  console.log('🔍 [Programs API] Full API URL:', apiUrl);
  console.log('🔍 [Programs API] Environment:', process.env.NODE_ENV);
  
  try {
    const response = await fetch(apiUrl);
    
    console.log('🔍 [Programs API] Response status:', response.status);
    console.log('🔍 [Programs API] Response headers:', Object.fromEntries(response.headers.entries()));
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ [Programs API] Response not OK:', response.status, response.statusText);
      console.error('❌ [Programs API] Error body:', errorText);
      throw new Error(`Failed to fetch services from backend: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    console.log('✅ [Programs API] Successfully fetched data:', {
      success: data.success,
      statusCode: data.statusCode,
      dataLength: data.data?.length || 0,
      services: data.data?.map((s: any) => ({ name: s.name, key: s.key, hasImage: !!s.image })) || []
    });
    
    return data;
  } catch (error) {
    console.error('❌ [Programs API] Fetch error:', error);
    throw error;
  }
}

/**
 * Converts legacy JSON format to backend format
 */
function convertLegacyToBackendFormat(legacyData: any): BackendServicesResponse {
  return {
    success: true,
    statusCode: 200,
    message: "Services retrieved successfully",
    data: legacyData.programs.map((program: any) => ({
      id: program.id.toString(),
      name: program.title,
      key: program.title.toLowerCase().replace(/\s+/g, '-'),
      description: program.description,
      showOnSite: true,
      isActive: true,
      isDeleted: false,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    })),
    meta: {
      requestId: "fallback-request-id",
      timestamp: new Date().toISOString(),
      duration: 0,
      pagination: {
        page: 1,
        limit: legacyData.programs.length.toString(),
        total: legacyData.programs.length,
        totalPages: 1,
        hasNextPage: false,
        hasPrevPage: false
      },
      path: "/api/v1/services",
      method: "GET",
      userAgent: "Fallback-Data",
      ip: "127.0.0.1"
    }
  };
}

/**
 * Server-side function to get programs data from backend
 */
export async function getProgramsData(): Promise<BackendServicesResponse> {
  console.log('🔍 [Programs API] Server-side fetch starting...');
  console.log('🔍 [Programs API] NODE_ENV:', process.env.NODE_ENV);
  console.log('🔍 [Programs API] NEXT_PHASE:', process.env.NEXT_PHASE);
  console.log('🔍 [Programs API] NEXT_PUBLIC_API_BACKEND:', process.env.NEXT_PUBLIC_API_BACKEND);
  
  try {
    // Always try to fetch from real backend first
    const backendUrl = process.env.NEXT_PUBLIC_API_BACKEND || 'https://sca-backend.digitaldevworkspace.com';
    const apiUrl = `${backendUrl}/api/v1/services?limit=3`;
    
    console.log('🔍 [Programs API] Server-side backend URL:', backendUrl);
    console.log('🔍 [Programs API] Server-side full API URL:', apiUrl);
    
    const response = await serverFetch(apiUrl, {
      cache: 'no-store', // Ensure fresh data
    });
    
    console.log('🔍 [Programs API] Server response status:', response.status);
    console.log('🔍 [Programs API] Server response headers:', Object.fromEntries(response.headers.entries()));
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ [Programs API] Server response not OK:', response.status, response.statusText);
      console.error('❌ [Programs API] Server error body:', errorText);
      throw new Error(`Failed to fetch services from backend: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    console.log('✅ [Programs API] Server-side fetch successful:', {
      success: data.success,
      statusCode: data.statusCode,
      dataLength: data.data?.length || 0,
      services: data.data?.map((s: any) => ({ name: s.name, key: s.key, hasImage: !!s.image })) || []
    });
    
    return data;
  } catch (error) {
    console.error('❌ [Programs API] Server-side error:', error);
    console.log('🔍 [Programs API] Attempting fallback to JSON import...');
    
    // Fallback to direct import if fetch fails
    try {
      const programsData = await import('@/data/programs.json');
      const converted = convertLegacyToBackendFormat(programsData.default);
      console.log('✅ [Programs API] Fallback successful:', {
        success: converted.success,
        dataLength: converted.data?.length || 0
      });
      return converted;
    } catch (importError) {
      console.error('❌ [Programs API] Fallback also failed:', importError);
      throw error;
    }
  }
}
